import {
  TimeUnit,
  type MessageChannelV3,
} from "@redotech/redo-flows-common/rules-v3/types";
import { AssignV3Strategy } from "@redotech/redo-flows-common/rules/actions";
import { AssignmentTypeOption } from "@redotech/redo-flows-common/rules/schemas";
import {
  CSAT_SURVEY_METHODS,
  CSAT_SURVEY_SEND_TIME_OPTIONS,
  CsatSurveyMethod,
  SurveySendTimeOptions,
  type CsatSurveySendTime,
  type CsatSurveyVariant,
} from "@redotech/redo-support-settings-common/csat-survey";
import type { ArchiveEmailsFromBlockedSendersConfig } from "./rules/archive-emails-from-blocked-senders";
import type { AssignAiOnConversationCreatedConfig } from "./rules/assign-ai-on-conversation-created";
import type { AssignOnConversationCreatedConfig } from "./rules/assign-on-conversation-created";
import type { AssignOnMerchantResponseConfig } from "./rules/assign-on-merchant-response";
import type { AutoMergeOnConversationCreatedConfig } from "./rules/auto-merge-on-conversation-created";
import type { SendAiResponseOnCustomerReplyConfig } from "./rules/send-ai-response-on-customer-reply";
import type { SendAiResponseOnShopperAgentConversationConfig } from "./rules/send-ai-response-on-shopper-agent-conversation";
import type { SendCsatSurveyOnConversationClosedConfig } from "./rules/send-csat-survey-on-conversation-closed";
import type { StopTicketContinuationOnAutoSplitSenderConfig } from "./rules/stop-ticket-continuation-on-auto-split-sender";
import { SHOPPER_AGENT_EMAIL_REPLY_CHANNELS } from "./rules/unassign-ai-on-shopper-agent-email-conversation";

/**
 * Pure translation helpers from legacy `team.settings.support.*` shapes to
 * the default-rule config shapes written to CockroachDB for support v2
 * teams. No RPC, no React — the settings pages that already save to
 * Mongo import these and pipe the result into `useSyncDefaultRuleConfig`.
 *
 * Return types are the rule config types imported from the shared
 * `default-rules/rules/*` files, so a future change to a rule's
 * `configSchema` surfaces here as a type error.
 */

/**
 * Subset of the merchant-support ticket-rules form that feeds both the
 * assign-on-conversation-created and unassign-on-conversation-reopened
 * default rules. Callers pass just the relevant fields — decoupled from
 * the `TicketRulesValue` form type that lives in `merchant/support/app`.
 */
export interface TicketRulesSyncInput {
  readonly ticketAssignment: "manual" | "round-robin" | "balanced";
  readonly usersForAutoAssignment: readonly string[];
  readonly maxTicketsPerUser: string;
  readonly keepAssigneeOnReopen: boolean;
  /**
   * Legacy `team.settings.support.autoAssignOnResponse` — base case for the
   * assign-on-merchant-response default rule. When `false` the rule is
   * disabled outright (the two reassign flags below have no effect).
   */
  readonly autoAssignOnResponse: boolean;
  /**
   * Legacy `team.settings.support.autoReassignFromAiOnResponse` — widens the
   * assign-on-merchant-response rule to also fire when the conversation is
   * currently assigned to the AI agent.
   */
  readonly autoReassignFromAiOnResponse: boolean;
  /**
   * Legacy `team.settings.support.autoReassignFromHumanOnResponse` — widens
   * the assign-on-merchant-response rule to also fire when the conversation
   * is currently assigned to a human user.
   */
  readonly autoReassignFromHumanOnResponse: boolean;
}

function parseMaxTicketsPerUser(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * `ticketAssignment === "manual"` disables the rule. The mode (users) and
 * maxTicketsPerUser are still written so flipping back to round-robin or
 * balanced reuses the last configuration.
 */
export function ticketRulesToAssignConfig(value: TicketRulesSyncInput): {
  defaultRuleId: "default-assign-on-conversation-created";
  enabled: boolean;
  configurations: AssignOnConversationCreatedConfig;
} {
  const strategy =
    value.ticketAssignment === "balanced"
      ? AssignV3Strategy.BALANCED
      : AssignV3Strategy.ROUND_ROBIN;

  return {
    defaultRuleId: "default-assign-on-conversation-created",
    enabled: value.ticketAssignment !== "manual",
    configurations: {
      mode: {
        _tag: "specific_users",
        mongoUserIds: value.usersForAutoAssignment,
      },
      strategy,
      maxTicketsPerUser: parseMaxTicketsPerUser(value.maxTicketsPerUser),
    },
  };
}

/**
 * Maps the three legacy response-assignment toggles to the
 * assign-on-merchant-response default rule.
 *
 * - `autoAssignOnResponse` is the master switch — when `false`, the rule
 *   is disabled wholesale. When `true`, the rule's single
 *   `ASSIGNMENT IS [...]` condition always includes `NO_ASSIGNEE` (the
 *   base "assign on response when unassigned" behaviour).
 * - `autoReassignFromAiOnResponse` widens the array to include
 *   `AI_AGENT_ASSIGNEE` (also reassign when the AI agent currently owns
 *   the conversation).
 * - `autoReassignFromHumanOnResponse` widens the array to include
 *   `SUPPORT_AGENT_ASSIGNEE` (also reassign when a human user currently
 *   owns the conversation).
 *
 * `assigneeTypes` is fixed at `["USER"]` so only human merchants — not AI
 * replies — trigger (re)assignment. Merchants who want a different actor
 * allowlist can edit the rule directly.
 */
export function responseAssignmentRulesToConfig(
  value: Pick<
    TicketRulesSyncInput,
    | "autoAssignOnResponse"
    | "autoReassignFromAiOnResponse"
    | "autoReassignFromHumanOnResponse"
  >,
): {
  defaultRuleId: "default-assign-on-merchant-response";
  enabled: boolean;
  configurations: AssignOnMerchantResponseConfig;
} {
  const assignmentTypes: AssignmentTypeOption[] = [
    AssignmentTypeOption.NO_ASSIGNEE,
  ];
  if (value.autoReassignFromAiOnResponse) {
    assignmentTypes.push(AssignmentTypeOption.AI_AGENT_ASSIGNEE);
  }
  if (value.autoReassignFromHumanOnResponse) {
    assignmentTypes.push(AssignmentTypeOption.SUPPORT_AGENT_ASSIGNEE);
  }

  return {
    defaultRuleId: "default-assign-on-merchant-response",
    enabled: value.autoAssignOnResponse,
    configurations: { assigneeTypes: ["USER"], assignmentTypes },
  };
}

/**
 * The unassign-on-reopen default rule has no `configSchema`, so its
 * configurations blob is always the empty object `{}`. The legacy
 * setting is inverted (keep → don't unassign).
 */
export function ticketRulesToUnassignEnabled(
  value: Pick<TicketRulesSyncInput, "keepAssigneeOnReopen">,
): {
  defaultRuleId: "default-unassign-on-conversation-reopened";
  enabled: boolean;
  configurations: Record<string, never>;
} {
  return {
    defaultRuleId: "default-unassign-on-conversation-reopened",
    enabled: !value.keepAssigneeOnReopen,
    configurations: {},
  };
}

/**
 * Empty list → rule disabled (archiving nothing has no effect anyway).
 * Non-empty → enabled with the list preserved verbatim.
 */
export function excludedEmailsToArchiveConfig(list: readonly string[]): {
  defaultRuleId: "default-archive-emails-from-blocked-senders";
  enabled: boolean;
  configurations: ArchiveEmailsFromBlockedSendersConfig;
} {
  return {
    defaultRuleId: "default-archive-emails-from-blocked-senders",
    enabled: list.length > 0,
    configurations: { blockedEmails: list },
  };
}

export interface CsatSurveySyncInput {
  readonly enabled: boolean;
  readonly surveySendTime?: string | null;
  readonly surveyMethods?: readonly string[] | null;
  readonly allowRemarks: boolean;
  readonly headerImage?: string;
  readonly surveyMessage?: string;
  readonly allowedInboxEmails?: readonly string[] | null;
  readonly abTestEnabled?: boolean;
  readonly abTestVariants?: readonly CsatSurveyVariant[];
}

const CSAT_SURVEY_METHOD_SET: ReadonlySet<string> = new Set<string>(
  CSAT_SURVEY_METHODS,
);
const CSAT_SURVEY_SEND_TIME_SET: ReadonlySet<string> = new Set<string>(
  CSAT_SURVEY_SEND_TIME_OPTIONS,
);

const isCsatSurveyMethod = (value: string): value is CsatSurveyMethod =>
  CSAT_SURVEY_METHOD_SET.has(value);

const isCsatSurveySendTime = (value: string): value is CsatSurveySendTime =>
  CSAT_SURVEY_SEND_TIME_SET.has(value);

const toCsatSurveySendTime = (
  value: string | null | undefined,
): CsatSurveySendTime =>
  value && isCsatSurveySendTime(value) ? value : SurveySendTimeOptions.ONE_HOUR;

export function csatSettingsToSendSurveyConfig(value: CsatSurveySyncInput): {
  defaultRuleId: "default-send-csat-survey-on-conversation-closed";
  enabled: boolean;
  configurations: SendCsatSurveyOnConversationClosedConfig;
} {
  const surveyMethods = (value.surveyMethods ?? []).filter(isCsatSurveyMethod);

  return {
    defaultRuleId: "default-send-csat-survey-on-conversation-closed",
    enabled: value.enabled && surveyMethods.includes(CsatSurveyMethod.EMAIL),
    configurations: {
      surveySendTime: toCsatSurveySendTime(value.surveySendTime),
      surveyMethods,
      allowRemarks: value.allowRemarks,
      headerImage: value.headerImage ?? "",
      surveyMessage: value.surveyMessage ?? "",
      allowedInboxEmails: value.allowedInboxEmails ?? [],
      abTestEnabled: value.abTestEnabled ?? false,
      abTestVariants: value.abTestVariants ?? [],
    },
  };
}

/**
 * Subset of `team.settings.support.ai` that controls the AI auto-reply
 * channels. Missing / undefined booleans are treated as `false`.
 */
export interface AiSyncInput {
  readonly autopilotEnabled: boolean;
  readonly automateLiveChat?: boolean;
  readonly enabledPlatforms: {
    readonly email?: boolean;
    readonly facebook?: boolean;
    // Public comments gated independently of DMs so AI can run on DMs only.
    readonly facebookComments?: boolean;
    readonly instagram?: boolean;
    readonly instagramComments?: boolean;
    readonly sms?: boolean;
    readonly attentive?: boolean;
    readonly postscript?: boolean;
    readonly tiktok?: boolean;
  };
}

/**
 * Maps legacy AI platform booleans to rules-v3 `MessageChannelV3` values.
 * Order matches the order enabledPlatforms fields are read in, so outputs
 * stay stable and diffable. A single legacy toggle may fan out to multiple
 * channel values (e.g. `email` → `gmail` + `outlook`).
 */
export function aiSettingsToAiReplyConfig(input: AiSyncInput): {
  defaultRuleId: "default-send-ai-response-on-customer-reply";
  enabled: boolean;
  configurations: SendAiResponseOnCustomerReplyConfig;
} {
  const channels: MessageChannelV3[] = [];
  if (input.automateLiveChat) {
    channels.push("redoChat");
  }
  if (input.autopilotEnabled) {
    if (input.enabledPlatforms.email) {
      channels.push("gmail", "outlook", "customEmail", "contactForm");
    }
    if (input.enabledPlatforms.facebook) {
      channels.push("facebook");
    }
    if (input.enabledPlatforms.facebookComments) {
      channels.push("facebookComments");
    }
    if (input.enabledPlatforms.instagram) {
      channels.push("instagram");
    }
    if (input.enabledPlatforms.instagramComments) {
      channels.push("instagramComments");
    }
    if (input.enabledPlatforms.sms) {
      channels.push("sms");
    }
    if (input.enabledPlatforms.attentive) {
      channels.push("attentive");
    }
    if (input.enabledPlatforms.postscript) {
      channels.push("postscript");
    }
    if (input.enabledPlatforms.tiktok) {
      channels.push("tiktok");
    }
  }

  return {
    defaultRuleId: "default-send-ai-response-on-customer-reply",
    enabled: input.autopilotEnabled || !!input.automateLiveChat,
    configurations: { channels },
  };
}

/**
 * Maps the legacy `autoMergeEnabled` toggle and `autoMergeExclusions` list to
 * the auto-merge default rule. The rule fires for all channels; the exclusion
 * list is wired to a SENT_FROM DOES_NOT_CONTAIN condition so excluded email
 * senders are skipped, while non-email channels pass through unconditionally.
 */
export function autoMergeToConfig(
  enabled: boolean,
  exclusions: readonly string[],
  maxAgeSinceLastMessage: AutoMergeOnConversationCreatedConfig["maxAgeSinceLastMessage"] = {
    value: 24,
    unit: TimeUnit.HOURS,
  },
): {
  defaultRuleId: "default-auto-merge-on-conversation-created";
  enabled: boolean;
  configurations: AutoMergeOnConversationCreatedConfig;
} {
  return {
    defaultRuleId: "default-auto-merge-on-conversation-created",
    enabled,
    configurations: {
      autoMergeExclusions: [...exclusions],
      maxAgeSinceLastMessage,
    },
  };
}

/**
 * Empty list → rule disabled (no senders to split on).
 * Non-empty → enabled with the list preserved verbatim.
 */
export function autoSplitSendersToStopTicketContinuationConfig(
  list: readonly string[],
): {
  defaultRuleId: "default-stop-ticket-continuation-on-auto-split-sender";
  enabled: boolean;
  configurations: StopTicketContinuationOnAutoSplitSenderConfig;
} {
  return {
    defaultRuleId: "default-stop-ticket-continuation-on-auto-split-sender",
    enabled: list.length > 0,
    configurations: { autoSplitSenders: [...list] },
  };
}

export function instagramAutoCloseMentionsToConfig(enabled: boolean): {
  defaultRuleId: "default-close-instagram-story-mentions";
  enabled: boolean;
  configurations: Record<string, never>;
} {
  return {
    defaultRuleId: "default-close-instagram-story-mentions",
    enabled,
    configurations: {},
  };
}

export function instagramAutoCloseRepliesToConfig(enabled: boolean): {
  defaultRuleId: "default-close-instagram-story-replies";
  enabled: boolean;
  configurations: Record<string, never>;
} {
  return {
    defaultRuleId: "default-close-instagram-story-replies",
    enabled,
    configurations: {},
  };
}

export function aiSettingsToAssignAiOnCreatedConfig(input: AiSyncInput): {
  defaultRuleId: "default-assign-ai-on-conversation-created";
  enabled: boolean;
  configurations: AssignAiOnConversationCreatedConfig;
} {
  const channels: MessageChannelV3[] = [];
  if (input.automateLiveChat) {
    channels.push("redoChat");
  }
  if (input.autopilotEnabled) {
    if (input.enabledPlatforms.email) {
      channels.push("gmail", "outlook", "customEmail", "contactForm");
    }
    if (input.enabledPlatforms.facebook) {
      channels.push("facebook");
    }
    if (input.enabledPlatforms.facebookComments) {
      channels.push("facebookComments");
    }
    if (input.enabledPlatforms.instagram) {
      channels.push("instagram");
    }
    if (input.enabledPlatforms.instagramComments) {
      channels.push("instagramComments");
    }
    if (input.enabledPlatforms.sms) {
      channels.push("sms");
    }
    if (input.enabledPlatforms.attentive) {
      channels.push("attentive");
    }
    if (input.enabledPlatforms.postscript) {
      channels.push("postscript");
    }
    if (input.enabledPlatforms.tiktok) {
      channels.push("tiktok");
    }
  }

  return {
    defaultRuleId: "default-assign-ai-on-conversation-created",
    enabled: input.autopilotEnabled || !!input.automateLiveChat,
    configurations: { channels },
  };
}

/**
 * Shopper Agent email replies → the agent rule's channel list. SMS is
 * unconditional (RCS and iMessage normalize to it); the email transports join
 * only on an explicit opt-in, so an unset team keeps answering SMS alone.
 */
export function shopperAgentEmailRepliesToAgentReplyConfig(
  emailRepliesEnabled: boolean,
): {
  defaultRuleId: "default-send-ai-response-on-shopper-agent-conversation";
  enabled: true;
  configurations: SendAiResponseOnShopperAgentConversationConfig;
} {
  const channels: MessageChannelV3[] = ["sms"];
  if (emailRepliesEnabled) {
    channels.push(...SHOPPER_AGENT_EMAIL_REPLY_CHANNELS);
  }
  return {
    defaultRuleId: "default-send-ai-response-on-shopper-agent-conversation",
    // The rule always runs; the channel list is the only thing that varies.
    enabled: true,
    configurations: { channels },
  };
}

/**
 * Shopper Agent email replies → the release rule's `enabled`. The inverse of
 * the reply rule's email opt-in: while replies are off, an AI-owned email lane
 * is unassigned on the customer's next email instead of answered.
 */
export function shopperAgentEmailRepliesToUnassignAiConfig(
  emailRepliesEnabled: boolean,
): {
  defaultRuleId: "default-unassign-ai-on-shopper-agent-email-conversation";
  enabled: boolean;
  configurations: Record<string, never>;
} {
  return {
    defaultRuleId: "default-unassign-ai-on-shopper-agent-email-conversation",
    enabled: !emailRepliesEnabled,
    configurations: {},
  };
}

/**
 * Both projections of the one setting. Writers go through this so a toggle
 * can never leave the reply rule and the release rule disagreeing about email.
 */
export function shopperAgentEmailRepliesToDefaultRuleConfigs(
  emailRepliesEnabled: boolean,
): readonly [
  ReturnType<typeof shopperAgentEmailRepliesToAgentReplyConfig>,
  ReturnType<typeof shopperAgentEmailRepliesToUnassignAiConfig>,
] {
  return [
    shopperAgentEmailRepliesToAgentReplyConfig(emailRepliesEnabled),
    shopperAgentEmailRepliesToUnassignAiConfig(emailRepliesEnabled),
  ];
}
